import { OverlappingJobs } from './overlappingJobs.svelte';
import type { Job, SchedularVehicleAssignment } from './types';

const EXCLUSIVE_TYPES = ['erect', 'full strike'];

export type ValidationStoreInner = {
    unscheduledJobs: Job[];
    overlappingJobs: Set<number>;
    missingJobType: SchedularVehicleAssignment[];
    missingPurpose: SchedularVehicleAssignment[];
    duplicateExclusiveType: SchedularVehicleAssignment[];
};

const resolveJobType = (va: SchedularVehicleAssignment): string | null => {
    const jt = va.job_type as unknown;
    if (typeof jt === 'object' && jt !== null) return (jt as { value: string }).value;
    if (typeof jt === 'string') return jt;
    return null;
};

export const validateJobs = (jobs: Map<string, Job>, overlappingJobs: OverlappingJobs) => {
    const errors: ValidationStoreInner = {
        overlappingJobs: new Set(),
        unscheduledJobs: [],
        missingJobType: [],
        missingPurpose: [],
        duplicateExclusiveType: [],
    };

    // Detect duplicate erect/full strike VAs per (job, phase). Post the
    // "phases as source of truth" plan, every erect/strike VA has a phase
    // id — the legacy NULL-primary slot is gone.
    const seenExclusive = new Map<string, SchedularVehicleAssignment>();
    for (const job of jobs.values()) {
        if (job.deleted) continue;
        const va = job as SchedularVehicleAssignment;
        const jobType = resolveJobType(va);
        if (!jobType || !EXCLUSIVE_TYPES.includes(jobType)) continue;
        const jobId = va.job?.id;
        if (!jobId) continue;
        const phaseId = (va as any).job_phase_id ?? 'primary';
        const key = `${jobId}__${phaseId}__${jobType}`;
        if (seenExclusive.has(key)) {
            // Both the first and subsequent duplicate should be flagged
            const first = seenExclusive.get(key)!;
            if (!errors.duplicateExclusiveType.includes(first)) {
                errors.duplicateExclusiveType.push(first);
            }
            errors.duplicateExclusiveType.push(va);
        } else {
            seenExclusive.set(key, va);
        }
    }

    // All jobs must be scheduled
    for (const job of jobs.values()) {
        if (job.start === null || job.duration === null) errors.unscheduledJobs.push(job);

        // Check for missing job_type (only for scheduled jobs that have been modified)
        if (job.modified && job.start !== null && (!job.job_type || job.job_type.trim() === '')) {
            errors.missingJobType.push(job as SchedularVehicleAssignment);
        }

        // Check for missing purpose (only for site visits that have been modified)
        const isSiteVisit = 'visitable_type' in job;
        if (isSiteVisit && job.modified && job.start !== null) {
            const siteVisit = job as any;
            if (!siteVisit.purpose || siteVisit.purpose.trim() === '') {
                errors.missingPurpose.push(job as SchedularVehicleAssignment);
            }
        }
    }

    return errors;
};